import type { PostHog } from 'posthog-js';
import { readConsent } from './consent';

/**
 * Analytics and crash reports, behind consent (plan docs 01, 17, 20).
 *
 * Nothing loads until the learner has said yes: PostHog and Sentry are
 * dynamic imports made only from `applyAnalyticsConsent`, so a "no" costs no
 * bytes and no requests. Both are also off in development, off for minors
 * whatever the stored answer, and off when their keys are not configured.
 *
 * What is sent is deliberately narrow. Events are named here and carry a
 * handful of numbers and ids — a session kind, an instrument, an accuracy —
 * and never an answer, a prompt, or anything from the microphone. Sentry gets
 * the exception and the route, with the user object stripped before send.
 */

export type AnalyticsEvent =
  | 'lesson_completed'
  | 'practice_completed'
  | 'song_played'
  | 'install_accepted'
  | 'certificate_earned';

export type EventProps = Record<string, string | number | boolean>;

let posthog: PostHog | null = null;
let sentryOn = false;

export function analyticsConfigured(): boolean {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  return typeof key === 'string' && key.length > 0;
}

export function errorReportsConfigured(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return typeof dsn === 'string' && dsn.length > 0;
}

/** True when there is anything the consent prompt could switch on. */
export function anythingConfigured(): boolean {
  return analyticsConfigured() || errorReportsConfigured();
}

/**
 * Brings the two clients in line with the stored answer. Called at boot and
 * whenever the answer changes; idempotent.
 */
export async function applyAnalyticsConsent(options: { minor: boolean }): Promise<void> {
  const consent = readConsent();
  const allowed = !options.minor && !import.meta.env.DEV;
  const wantAnalytics = allowed && consent.analytics === 'granted' && analyticsConfigured();
  const wantErrors = allowed && consent.errorReports === 'granted' && errorReportsConfigured();

  if (wantAnalytics && posthog === null) {
    const { default: client } = await import('posthog-js');
    client.init(import.meta.env.VITE_POSTHOG_KEY as string, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
      defaults: '2026-08-30',
      persistence: 'localStorage',
      // Only the events named in this file; nothing clicked is recorded, and
      // no page views: a view of `/u/<username>` would carry someone else's
      // name, and the named events already say which screens matter.
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      capture_exceptions: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      // No person profiles: the learner is a distinct id, not a record.
      person_profiles: 'never',
      respect_dnt: true,
      ip: false,
    });
    // A previous "no" is remembered by the client itself; a new "yes" undoes it.
    client.opt_in_capturing();
    posthog = client;
  } else if (!wantAnalytics && posthog !== null) {
    posthog.opt_out_capturing();
    posthog = null;
  }

  if (wantErrors && !sentryOn) {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN as string,
      release: `audigoal@${import.meta.env.VITE_APP_VERSION ?? '0.0.0'}`,
      environment: 'production',
      sendDefaultPii: false,
      tracesSampleRate: 0,
      beforeSend(event) {
        delete event.user;
        if (event.request !== undefined) delete event.request.headers;
        return event;
      },
    });
    sentryOn = true;
  } else if (!wantErrors && sentryOn) {
    const Sentry = await import('@sentry/react');
    await Sentry.close();
    sentryOn = false;
  }
}

/** Records an event, if the learner has said yes. Otherwise nothing. */
export function track(event: AnalyticsEvent, props?: EventProps): void {
  posthog?.capture(event, props);
}

/** Sends an error, if the learner has said yes. Otherwise nothing. */
export function reportError(error: unknown, extra?: Record<string, unknown>): void {
  if (!sentryOn) return;
  void import('@sentry/react').then((Sentry) =>
    Sentry.captureException(error, extra === undefined ? undefined : { extra }),
  );
}

/** What is currently switched on, for Settings and for tests. */
export function analyticsStatus(): { analytics: boolean; errorReports: boolean } {
  return { analytics: posthog !== null, errorReports: sentryOn };
}
